import { Flex } from 'foundations-components/components/Flex';
import { DashboardPage } from 'foundations-components/transitional/components/DashboardPage';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { KEY_PREFIX, useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { UsageScreenProvider, useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getMarketplaceInteractionEvent, getPageViewedEvent } from 'marketplace-ui-tracking/events/marketplace';
import { useEffect, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { LazyImageProvider } from 'trellis-ecosystem';
import { SuggestedCollections } from '../collections/components/SuggestedCollections';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { SearchResultsPageHeader } from '../components/headers/SearchResultsPageHeader';
import { Paths } from '../constants/paths';
import { Screens, Subscreens } from '../constants/usage';
import { FilterPanel } from '../filters/components/FilterPanel';
import { FilterPanelButton } from '../filters/components/FilterPanelButton';
import { FilterTags } from '../filters/components/FilterTags';
import { StorefrontFilterBar } from '../filters/components/StorefrontFilterBar';
import { useFilterConfig } from '../filters/hooks/useFilterConfig';
import { ScrollToTopOnMount } from '../hooks/useScrollToTop';
import { SearchResultsPageMetadata } from './SearchResultsPageMetadata';
import { ResultsGrid } from './components/ResultsGrid';
import { SearchResultsHeader } from './components/SearchResultsHeader';
import { SearchResultsPaginator } from './components/SearchResultsPaginator';
import { SortDropdown } from './components/SortDropdown';
import { useCollectionQuery } from './hooks/useCollectionQuery';
import { useParsedSearchResultsPathParams } from './hooks/useParsedSearchResultsPathParams';
import { useSearchResultsQuery } from './hooks/useSearchResultsQuery';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function SearchResultsPage({
  productType
}) {
  return /*#__PURE__*/_jsx(UsageScreenProvider, {
    screen: Screens.SEARCH_RESULTS,
    children: /*#__PURE__*/_jsx(SearchResultsPageContent, {
      productType: productType
    })
  });
}
function SearchResultsPageContent({
  productType
}) {
  const {
    data,
    loading
  } = useSearchResultsQuery();
  const collectionResult = useCollectionQuery();
  const filterConfig = useFilterConfig();
  const parsedPathParams = useParsedSearchResultsPathParams();
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const {
    mobile
  } = useResponsiveProps({
    mobile: {
      xs: true,
      lg: false
    }
  });
  const params = useSearchParams();
  const sorts = params.get('sort');
  useEffect(() => {
    if (!loading) {
      var _collectionResult$dat, _collectionResult$dat2;
      trackMarketplaceEvent(getPageViewedEvent(Object.assign({
        resultCount: data === null || data === void 0 ? void 0 : data.result.total,
        sorts: [sorts]
      }, !collectionResult.isUmbrellaCollection && {
        category: (_collectionResult$dat = collectionResult.data) === null || _collectionResult$dat === void 0 ? void 0 : _collectionResult$dat.slug,
        collectionId: (_collectionResult$dat2 = collectionResult.data) === null || _collectionResult$dat2 === void 0 ? void 0 : _collectionResult$dat2.id
      })));
    }
  }, [trackMarketplaceEvent, data, collectionResult, loading, sorts]);
  if (collectionResult.loading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      size: "medium",
      marginBlockStart: "1600"
    });
  }
  if (collectionResult.error || filterConfig.error) {
    return /*#__PURE__*/_jsx(ErrorMessage, {
      type: "badRequest"
    });
  }

  // this means we came from an SEO-optimized URL and need to redirect
  if (productType || Object.keys(parsedPathParams).length > 0) {
    return /*#__PURE__*/_jsx(ConvertedPathRedirector, {
      parsedPathParams: parsedPathParams,
      productType: productType
    });
  }
  return /*#__PURE__*/_jsx(DashboardPage, {
    headerComponent: /*#__PURE__*/_jsx(SearchResultsPageHeader, {}),
    children: /*#__PURE__*/_jsxs(ErrorBoundary, {
      children: [/*#__PURE__*/_jsx(SearchResultsPageMetadata, {}), /*#__PURE__*/_jsx(ScrollToTopOnMount, {}), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "start",
        marginBlockStart: "200",
        children: [/*#__PURE__*/_jsxs(Flex, {
          flex: 1,
          gap: "600",
          flexDirection: "column",
          maxInlineSize: "100%",
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "start",
            flexDirection: "column",
            flexWrap: "wrap",
            gap: "400",
            children: [/*#__PURE__*/_jsx(SearchResultsHeader, {}), mobile ? /*#__PURE__*/_jsxs(Flex, {
              alignItems: "start",
              gap: "800",
              inlineSize: "100%",
              marginBlockStart: "400",
              justifyContent: "space-between",
              children: [/*#__PURE__*/_jsx(SortDropdown, {}), /*#__PURE__*/_jsx(FilterPanelButton, {
                onFilterPanelOpen: () => setFilterPanelOpen(true)
              })]
            }) : /*#__PURE__*/_jsx(StorefrontFilterBar, {
              filterPanelOpen: filterPanelOpen,
              onFilterPanelOpen: () => setFilterPanelOpen(true)
            })]
          }), /*#__PURE__*/_jsx(FilterTags, {}), /*#__PURE__*/_jsx(LazyImageProvider, {
            children: /*#__PURE__*/_jsx(ResultsGrid, {})
          })]
        }), /*#__PURE__*/_jsx(FilterPanel, {
          open: filterPanelOpen,
          onClose: () => {
            trackMarketplaceEvent(getMarketplaceInteractionEvent({
              action: 'filter panel closed',
              subscreen: Subscreens.FILTER_PANEL
            }));
            setFilterPanelOpen(false);
          }
        })]
      }), /*#__PURE__*/_jsx(SearchResultsPaginator, {}), collectionResult.data && /*#__PURE__*/_jsx(SuggestedCollections, {})]
    })
  });
}

/** This is rendered when the URL is SEO-optimized, such as `/apps/abm`.
 * The user is immediately redirected to the query-param version of the URL. */
function ConvertedPathRedirector({
  parsedPathParams,
  productType
}) {
  const history = useHistory();
  const {
    AgentTeam,
    Category
  } = useFilterConfig();
  useEffect(() => {
    if (!(productType || Object.keys(parsedPathParams).length)) {
      return;
    }
    const urlParams = new URLSearchParams(history.location.search);
    if (productType) {
      urlParams.set(`${KEY_PREFIX}PRODUCT_TYPE`, productType);
    }
    if (parsedPathParams.page) {
      urlParams.set(`${KEY_PREFIX}page`, parsedPathParams.page.toString());
    }
    if (productType === 'APP' && parsedPathParams.categorySlug) {
      var _Category$options$fin;
      if (!(Category && Category.options.length)) return;

      /**
       * Convert the category slug to its enum value.
       */
      const categoryEnumValue = (_Category$options$fin = Category.options.find(o => o.slug === parsedPathParams.categorySlug)) === null || _Category$options$fin === void 0 ? void 0 : _Category$options$fin.value;
      if (categoryEnumValue) {
        urlParams.set(Category.fieldWithEcoPrefix, categoryEnumValue);
      }
      /**
       * Legacy URL redirects to the listing details page if the category slug is not found.
       * This is a temporary measure to ensure that the user is redirected to the correct page.
       * This will be removed in the future.
       */
      if (!categoryEnumValue) {
        history.replace(generatePath(Paths.LISTING_DETAILS, {
          listingSlug: parsedPathParams.categorySlug
        }));
        return;
      }
    }
    if (productType === 'AGENT' && parsedPathParams.teamSlug) {
      var _AgentTeam$options$fi;
      if (!(AgentTeam && AgentTeam.options.length)) return;

      /**
       * Convert the team slug to its enum value.
       */
      const teamEnumValue = (_AgentTeam$options$fi = AgentTeam.options.find(o => o.slug === parsedPathParams.teamSlug)) === null || _AgentTeam$options$fi === void 0 ? void 0 : _AgentTeam$options$fi.value;
      if (teamEnumValue) {
        urlParams.set(AgentTeam.fieldWithEcoPrefix, teamEnumValue);
      }
    }
    history.replace(`${Paths.SEARCH_RESULTS}?${urlParams.toString()}`);
  }, [AgentTeam, Category, history, parsedPathParams, productType]);
  return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true,
    size: "medium",
    marginBlockStart: "1600"
  });
}